/**
 * "Create an Idea" modal — Jira-style large proportions.
 *
 * Layout:
 *  ┌─────────────────────────────────────────────────┐  ← sticky header
 *  │  💡 Create an idea based on [option]        [✕] │
 *  ├─────────────────────────────────────────────────┤
 *  │  (scrollable body)                              │
 *  │  Title ___________________________________      │
 *  │                                                 │
 *  │  Description                                    │
 *  │  [B] [I] [H1] [H2] [ul] [ol] [code] ...        │  ← RichTextEditor toolbar
 *  │  ┌─────────────────────────────────────────┐   │
 *  │  │ ### Problem Statement                   │   │
 *  │  │ ...                                     │   │
 *  │  └─────────────────────────────────────────┘   │
 *  │                                                 │
 *  │  Product Area  [selector]                       │
 *  ├─────────────────────────────────────────────────┤
 *  │                         [Cancel] [Create Idea]  │  ← sticky footer
 *  └─────────────────────────────────────────────────┘
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkHtml from 'remark-html';
import { RichTextEditor, Link } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Modal,
  Box,
  Stack,
  TextInput,
  Button,
  Group,
  Text,
  Flex,
  Skeleton,
  Tooltip,
  ActionIcon,
} from '@mantine/core';
import { IconBulb, IconCheck, IconComponents, IconCopy, IconX } from '@tabler/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { showNotification } from '@mantine/notifications';
import { useFields } from '../../../hooks/useFields';
import { useKnowledgeData } from '../../../hooks/useKnowledgeData';
import BagelAccordionMenu from '../../../components/general/BagelAccordionMenu';
import { useQueryClient } from '@tanstack/react-query';
import { createNewIdea } from '../../../Api';
import { createOnePager, discoveryKeys } from '../../../api/discoveryAgent';
import { htmlToStructuredText } from '../../../utils/htmlToStructuredText';
import type { CreateIdeaOption } from './createIdeaFromChatPrompt';

// ── Markdown → HTML (for seeding the TipTap editor from ML output) ─────────
async function markdownToHtml(md: string): Promise<string> {
  const result = await unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkHtml, { sanitize: false })
    .process(md);
  return String(result);
}

// Escape text for safe interpolation into an HTML string (used for the copied
// title heading so special characters don't break the markup).
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export interface CreateIdeaFromChatModalProps {
  opened: boolean;
  onClose: () => void;
  selectedOption: CreateIdeaOption | null;
  fetchContent: () => Promise<{ title: any; description: any; alreadyPersisted?: boolean }>;
  ideaId: string;
  sessionId?: string;
  messageId?: string;
  persistOnePager?: boolean;
  /** When omitted, createdBy is not sent in the idea payload (e.g. Explore page). */
  createdBy?: string;
  /** Artifact _id to associate with the new idea (Explore page only). */
  artifactId?: string;
  onCreated?: (ideaId: string, title: string, description: string) => void;
  /**
   * e2e hook. This modal is mounted from five call sites (ExploreInsightsPanel,
   * ExploreMainContent, two in ExploreChatMessageList, ActiveSessionAndPrompts-
   * Carousel) and Mantine renders a Modal root even when `opened` is false, so
   * several roots coexist on one page. A shared testid therefore matches more
   * than one element and trips Playwright strict mode — give each call site its
   * own id instead.
   */
  modalTestId?: string;
}

export const CreateIdeaFromChatModal: React.FC<CreateIdeaFromChatModalProps> = ({
  opened,
  onClose,
  selectedOption,
  fetchContent,
  ideaId,
  sessionId,
  messageId,
  persistOnePager = true,
  createdBy,
  artifactId,
  onCreated,
  modalTestId,
}) => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState('');
  const [descError, setDescError] = useState('');

  const { allComponents } = useFields();
  const { defaultStatus, defaultPriority } = useKnowledgeData();
  const [productArea, setProductArea] = useState<any>(null);
  const [componentId, setComponentId] = useState('');
  const [componentName, setComponentName] = useState<string | null>(null);
  const [componentIdError, setComponentIdError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  // ── TipTap editor ──────────────────────────────────────────────────────
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link,
      Highlight,
      Placeholder.configure({ placeholder: 'Describe the idea — supports rich formatting…' }),
    ],
    content: '',
    onUpdate: ({ editor: e }) => {
      const text = e.getText();
      if (text.trim()) setDescError('');
    },
  });

  // Stores fetched HTML until the TipTap editor is ready to receive it
  const [pendingDescriptionHtml, setPendingDescriptionHtml] = useState<string | null>(null);
  // Guard: prevent double-invocation (e.g. React Strict Mode) from overwriting a successful prefill
  const hasFetchedRef = useRef(false);
  // Increments on every open; promise handlers compare against it so a slower
  // earlier fetch can't overwrite the content of a newer one (close+reopen race).
  const fetchGenerationRef = useRef(0);

  // Apply pendingDescriptionHtml as soon as the editor instance is available
  useEffect(() => {
    if (pendingDescriptionHtml !== null && editor) {
      editor.commands.setContent(pendingDescriptionHtml);
      setPendingDescriptionHtml(null);
    }
  }, [pendingDescriptionHtml, editor]);

  // Reset and fetch ML content when modal opens
  useEffect(() => {
    if (!opened) {
      hasFetchedRef.current = false;
      // Invalidate any in-flight fetch so it can't commit state after close.
      fetchGenerationRef.current += 1;
      return;
    }
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    // Capture this open's generation; only the latest one may commit state.
    const generation = ++fetchGenerationRef.current;
    const isStale = () => generation !== fetchGenerationRef.current;

    setTitle('');
    setTitleError('');
    setDescError('');
    setComponentIdError('');
    setProductArea(null);
    setComponentId('');
    setComponentName(null);
    setPendingDescriptionHtml(null);
    editor?.commands.setContent('');

    setIsLoadingContent(true);
    fetchContent()
      .then(async ({ title: t, description: d, alreadyPersisted }) => {
        if (isStale()) return;
        // Convert markdown from ML to HTML for the rich-text editor.
        // Store in state; a separate effect applies it once the editor is ready.
        let html: string;
        try {
          html = await markdownToHtml(d);
        } catch {
          // Fallback: set as plain paragraph
          html = `<p>${d.replace(/\n/g, '<br/>')}</p>`;
        }
        if (isStale()) return;
        setTitle(t);
        setPendingDescriptionHtml(html);
        const sid = sessionId?.trim();
        const mid = messageId?.trim();
        const hasContent = Boolean(t?.trim()) && Boolean(d?.trim());
        // Skip the POST when the content was just read from an existing saved
        // one-pager — re-POSTing it would be a redundant write (and risk a
        // duplicate/conflict) on every reopen.
        if (persistOnePager && !alreadyPersisted && hasContent && sid && mid) {
          createOnePager(
            {
              sessionId: sid,
              messageId: mid,
              queryText: selectedOption?.payload ?? '',
              title: t,
              description: d,
            },
            auth0,
          )
            .then((saved) => {
              queryClient.setQueryData(discoveryKeys.onePager(sid, mid), saved);
              // Also update the session-level cache so the "View one-pager" control
              // appears immediately without waiting for a refetch.
              queryClient.setQueryData<import('../../../api/discoveryAgent').OnePager[]>(
                discoveryKeys.sessionOnePagers(sid),
              (prev) => {
                if (!prev) return [saved];
                const idx = prev.findIndex(
                  (p) => p.messageId === saved.messageId && p.artifactId === saved.artifactId
                );
                if (idx !== -1) {
                  const next = [...prev];
                  next[idx] = saved;
                  return next;
                }
                return [...prev, saved];
              }
              );
            })
            .catch((e) => {
              console.error('[CreateIdeaFromChatModal] createOnePager failed', e);
            });
        }
      })
      .catch((err) => {
        if (isStale()) return;
        // An aborted generation means whoever asked for it is gone (the host
        // component unmounted, so this modal is unmounting too). There is no
        // failure to report and nobody left to read the notification.
        if ((err as { name?: string } | null)?.name === 'AbortError') return;
        console.error('[CreateIdeaFromChatModal] fetchContent failed', err);
        showNotification({
          title: 'Could not generate content',
          message: 'Fill in the title and description manually.',
          color: 'orange',
          autoClose: 3000,
        });
      })
      .finally(() => {
        if (isStale()) return;
        setIsLoadingContent(false);
      });
  }, [opened]);

  const handleProductAreaSelect = useCallback((item: any) => {
    setProductArea(item);
    setComponentId(item?._id ?? '');
    setComponentName(item?.name ?? null);
    setComponentIdError('');
  }, []);

  const validate = (): boolean => {
    let ok = true;
    if (!title.trim()) { setTitleError('Title is required'); ok = false; }
    else setTitleError('');
    const descText = editor?.getText()?.trim() ?? '';
    if (!descText) { setDescError('Description is required'); ok = false; }
    else setDescError('');
    if (!componentId) { setComponentIdError('Product area is required'); ok = false; }
    else setComponentIdError('');
    return ok;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const foundComponent = allComponents.find((c: any) => c._id === componentId);
    if (!foundComponent) { setComponentIdError('Please select a valid product area'); return; }

    setIsSubmitting(true);
    try {
      // The idea page renders description as plain text (stripHtml → Textarea/Text),
      // and stripHtml uses textContent which drops all block boundaries — so storing
      // raw editor HTML collapses headings/paragraphs/list items into one run-on line.
      // Serialize to structured text instead so headings, blank lines between blocks,
      // and bullet/number markers survive and the created idea reads like the modal.
      const structuredDescription = editor
        ? htmlToStructuredText(editor.getHTML())
        : '';
      const ideaPayload: Record<string, unknown> = {
        title: title.trim(),
        description: structuredDescription,
        componentId,
        ...(createdBy !== undefined && !artifactId ? { createdBy } : {}),
        ...(artifactId ? { artifactId } : {}),
        ownerId: (foundComponent as any).ownerId,
        rolloutDate: null,
        status: defaultStatus ?? 'draft',
        priority: defaultPriority ?? 'medium',
      };
      if (sessionId?.trim()) ideaPayload.sessionId = sessionId.trim();

      const response: any = await createNewIdea(
        { idea: ideaPayload, request: [], productItemId: null },
        auth0
      );
      const newIdeaId = response?.createNewIdeaRes?.idea?._id ?? response?.idea?._id;

      showNotification({
        title: 'Idea created',
        color: 'teal',
        icon: <IconCheck size="1.1rem" />,
        message: newIdeaId ? (
          <Text size="sm">
            <a href={`/idea/${newIdeaId}`} target="_blank" rel="noopener noreferrer">
              Open the new idea
            </a>
          </Text>
        ) : 'Your idea has been created.',
      });
      // Show idea created message in agent chat, then close the modal
      if (onCreated) {
        onCreated(newIdeaId ?? '', title.trim(), structuredDescription);
      }
      onClose();
    } catch (err) {
      console.error('[CreateIdeaFromChatModal] createNewIdea failed', err);
      showNotification({
        title: 'Failed to create idea',
        message: 'Please try again.',
        color: 'red',
        autoClose: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const optionLabel = selectedOption?.label ?? 'all insights in the session';
  const optionShort = optionLabel.length > 72 ? optionLabel.slice(0, 69) + '…' : optionLabel;

  const handleCopy = useCallback(() => {
    const trimmedTitle = title.trim();
    const descriptionHtml = editor?.getHTML() ?? '';
    // Use the structured-text serializer (not editor.getText()) so the plain
    // version keeps bullet/number markers and has exactly one blank line between
    // blocks. editor.getText() drops list markers and emits \n\n around every
    // node — including empty paragraphs — which is what produced the missing
    // bullets and the 3–4 blank lines when pasting into plain-text targets.
    const descriptionText = editor ? htmlToStructuredText(descriptionHtml) : '';

    // Rich (HTML) clipboard payload mirrors exactly what the modal renders:
    // the title as a heading + the editor's own HTML (headings, bold, lists…).
    // Pasting into rich-text targets (Docs, Word, email, Notion) keeps the
    // formatting; the text/plain alternative is the fallback for plain targets.
    const htmlToCopy = `<h2>${escapeHtml(trimmedTitle)}</h2>${descriptionHtml}`;
    const textToCopy = `${trimmedTitle}\n\n${descriptionText}`;

    const markCopied = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    const fallbackCopy = () => {
      const ta = document.createElement('textarea');
      ta.value = textToCopy;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try {
        document.execCommand('copy');
        markCopied();
      } catch {
        /* clipboard unavailable */
      }
      document.body.removeChild(ta);
    };

    // Prefer the async Clipboard API with both HTML + plain text so the exact
    // rendered format is preserved when supported.
    if (navigator.clipboard && typeof window.ClipboardItem !== 'undefined') {
      try {
        const item = new window.ClipboardItem({
          'text/html': new Blob([htmlToCopy], { type: 'text/html' }),
          'text/plain': new Blob([textToCopy], { type: 'text/plain' }),
        });
        navigator.clipboard.write([item]).then(markCopied).catch(fallbackCopy);
        return;
      } catch {
        /* fall through to plain-text paths */
      }
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(textToCopy).then(markCopied).catch(fallbackCopy);
      return;
    }

    fallbackCopy();
  }, [title, editor]);

  return (
    <Modal
      data-testid={modalTestId}
      opened={opened}
      onClose={onClose}
      size={720}
      centered
      closeOnClickOutside={!isSubmitting}
      zIndex={500}
      transitionProps={{ duration: 200, transition: 'pop' }}
      styles={{
        modal: {
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
        },
        body: {
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
        },
        header: {
          padding: '18px 24px 16px',
          borderBottom: '1px solid #E9ECEF',
          margin: 0,
          flexShrink: 0,
        },
        title: { fontWeight: 600, fontSize: 15, width: '100%' },
        close: { marginRight: 0 },
      }}
      title={
        <Flex align="center" gap={8} wrap="nowrap" w="100%">
          <IconBulb size={16} color="#5C5CEB" style={{ flexShrink: 0 }} />
          <Text size="sm" color="#585C68" sx={{ whiteSpace: 'nowrap' }}>
            Create a one-pager from 
          </Text>
          <Text
            size="sm"
            fw={600}
            color="#5C5CEB"
            title={optionLabel}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {optionShort}
          </Text>
        </Flex>
      }
    >
      {/* ── Scrollable content ── */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Stack spacing={20} px={24} pt={24} pb={8}>

          {/* Title */}
          {isLoadingContent ? (
            <Stack spacing={6}>
              <Text size="sm" color="#212529" sx={{ whiteSpace: 'nowrap' }} fw={600}>
                Title
                <Text span c="red" ml={2}>*</Text>
              </Text>
              {/* <Skeleton height={12} width={36} radius="sm" /> */}
              <Skeleton height={38} radius="sm" />
            </Stack>
          ) : (
            <TextInput
              label="Title"
              placeholder="A concise sentence summarising the main capability or solution"
              value={title}
              onChange={(e) => {
                setTitle(e.currentTarget.value);
                if (e.currentTarget.value.trim()) setTitleError('');
              }}
              error={titleError}
              required
              radius="md"
              styles={{
                label: { fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#212529' },
                input: { fontSize: 14, minHeight: 40 },
              }}
            />
          )}

          {/* Description — RichTextEditor */}
          <Box>
            <Text
              fz={13}
              fw={600}
              mb={6}
              color={descError ? 'red' : '#212529'}
            >
              Description
              <Text span c="red" ml={2}>*</Text>
            </Text>

            {isLoadingContent ? (
              <Stack spacing={7}>
                {[100, 88, 95, 78, 100, 83, 91, 76, 100, 88].map((w, i) => (
                  <Skeleton key={i} height={13} width={`${w}%`} radius="sm" />
                ))}
              </Stack>
            ) : (
              <Box
                sx={{
                  border: descError ? '1px solid #FA5252' : '1px solid #DEE2E6',
                  borderRadius: 8,
                  overflow: 'hidden',
                  '&:focus-within': {
                    borderColor: '#5C5CEB',
                    boxShadow: '0 0 0 2px rgba(92,92,235,0.12)',
                  },
                }}
              >
                <RichTextEditor
                  editor={editor}
                  styles={{
                    root: { border: 'none', borderRadius: 0 },
                    toolbar: {
                      borderBottom: '1px solid #E9ECEF',
                      borderRadius: 0,
                      padding: '6px 8px',
                      gap: 2,
                      backgroundColor: '#FAFAFA',
                    },
                    content: {
                      height: 350,
                      overflowY: 'auto',
                      fontSize: 14,
                      lineHeight: 1.65,
                      padding: '12px 16px',
                      backgroundColor: '#fff',
                      '& h1': { fontSize: 20, fontWeight: 700, marginTop: 16, marginBottom: 6 },
                      '& h2': { fontSize: 17, fontWeight: 700, marginTop: 14, marginBottom: 6 },
                      '& h3': { fontSize: 14, fontWeight: 600, marginTop: 12, marginBottom: 4, color: '#212529' },
                      '& p': { marginBottom: 8 },
                      '& ul, & ol': { paddingLeft: 20, marginBottom: 10 },
                      '& li': { marginBottom: 3 },
                      '& code': {
                        backgroundColor: '#F3F4F6',
                        borderRadius: 4,
                        padding: '2px 5px',
                        fontSize: 12,
                        fontFamily: 'monospace',
                      },
                      '& blockquote': {
                        borderLeft: '3px solid #5C5CEB',
                        paddingLeft: 12,
                        margin: '8px 0',
                        color: '#6C757D',
                      },
                    },
                  }}
                >
                  <RichTextEditor.Toolbar sticky stickyOffset={0}>
                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.Bold />
                      <RichTextEditor.Italic />
                      <RichTextEditor.Strikethrough />
                      <RichTextEditor.Highlight />
                      <RichTextEditor.Code />
                    </RichTextEditor.ControlsGroup>
                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.BulletList />
                      <RichTextEditor.OrderedList />
                    </RichTextEditor.ControlsGroup>
                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.Link />
                      <RichTextEditor.Unlink />
                    </RichTextEditor.ControlsGroup>
                  </RichTextEditor.Toolbar>

                  <RichTextEditor.Content />
                </RichTextEditor>
              </Box>
            )}

            {descError && (
              <Text size="xs" color="red" mt={4}>
                {descError}
              </Text>
            )}
          </Box>

          {/* Product Area */}
          <Box>
            <Flex align="center" mb={6} gap={6}>
              <IconComponents size={12} color="#585C68" />
              <Text fz={13} fw={600} color="#212529">
                Product Area
                <Text span c="red" ml={2}>*</Text>
              </Text>
            </Flex>

            {isLoadingContent ? (
              <Skeleton height={36} radius="md" width={240} />
            ) : (
              <>
                <BagelAccordionMenu
                  selectedComponent={productArea}
                  isAiSuggestion={false}
                  onSelect={(item: any, type: string) => {
                    if (type !== 'Domain') handleProductAreaSelect(item);
                  }}
                >
                  <Flex
                    h={36}
                    px={12}
                    py={4}
                    bg="#5C5CEB0D"
                    align="center"
                    sx={{
                      borderRadius: 6,
                      border: componentIdError ? '1px solid #FA5252' : '1px solid #DEE2E6',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      minWidth: 200,
                      maxWidth: 320,
                    }}
                  >
                    <Text fz={13} lineClamp={1} color={productArea?.name ? '#212529' : '#ADB5BD'}>
                      {productArea?.name ? (
                        <>
                          <Text span color="dimmed" fz={12}>…/ </Text>
                          {productArea.name}
                        </>
                      ) : (
                        'Select product area'
                      )}
                    </Text>
                  </Flex>
                </BagelAccordionMenu>
                {componentIdError && (
                  <Text size="xs" color="red" mt={4}>
                    {componentIdError}
                  </Text>
                )}
              </>
            )}
          </Box>

        </Stack>
      </Box>

      {/* ── Sticky footer ── */}
      <Box
        sx={{
          borderTop: '1px solid #E9ECEF',
          padding: '14px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          backgroundColor: '#fff',
        }}
      >
        {/* Copy to clipboard */}
        <Tooltip label={copied ? 'Copied!' : 'Copy'} withArrow>
          <ActionIcon
            variant="subtle"
            color={copied ? 'teal' : 'gray'}
            radius="md"
            size="lg"
            onClick={handleCopy}
            disabled={isLoadingContent}
            sx={{
              '&:hover': { backgroundColor: 'rgba(92,92,235,0.08)' },
            }}
          >
            {copied ? <IconCheck size={18} color="#12B886" /> : <IconCopy size={18} />}
          </ActionIcon>
        </Tooltip>

        {/* Right side: Cancel + Convert */}
        <Flex gap={10}>
          <Button
            variant="subtle"
            color="gray"
            radius="md"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Tooltip label="Convert this one-pager into a Bagel Idea">
            <Button
              radius="md"
              loading={isSubmitting}
              disabled={isLoadingContent || isSubmitting}
              onClick={handleSubmit}
              styles={{
                root: {
                  backgroundColor: '#5C5CEB',
                  '&:hover': { backgroundColor: '#4848c5' },
                  '&:disabled': { backgroundColor: '#E3E8FF', color: '#9999CC' },
                },
              }}
            >
            Convert to Idea
            </Button>
          </Tooltip>
        </Flex>
      </Box>
    </Modal>
  );
};

export default CreateIdeaFromChatModal;
